import React, { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  buildOrderedPublicProviderListRows,
  CAIRE_INDEX_OBSERVED_NATIONAL_CEILING_HINT_SV,
  EnhancedSeoHead,
  generateBreadcrumbSchema,
  generateItemListSchema,
  SeoFunnelLink,
  summarizePublicProviders,
  publicListSeoState,
} from "@appcaire/shared/seo";
import { ProviderList } from "@appcaire/shared/seo";
import { CaireBadge } from "@appcaire/shared/seo";
import { CertificationExplanation } from "@appcaire/shared/seo";
import {
  useGetProvidersForMunicipalityQuery,
  useGetMunicipalityQuery,
} from "@appcaire/graphql";
import {
  Users,
  ExternalLink,
  CheckCircle,
  ArrowRight,
  Info,
  TrendingUp,
  Award,
  BarChart3,
  Building2,
  DollarSign,
} from "lucide-react";
import {
  buildNackaProviderHref,
  NACKA_PROVIDER_CANONICAL_ORIGIN,
} from "../lib/providerRoutes";

const CAIRE_INDEX_METHODOLOGY_URL =
  "https://sverigeshemtjanst.se/topplistor/metodik";
// This site's own origin. Distinct from NACKA_PROVIDER_CANONICAL_ORIGIN, which
// is hemtjanstguide.se because provider *detail* pages deliberately
// consolidate there — using it for this list page pointed the canonical at
// another domain and asked Google to drop the page.
const SITE_ORIGIN = "https://nackahemtjanst.se";
const CANONICAL_URL = `${SITE_ORIGIN}/utforare`;
const DEFAULT_PAGE_SIZE = 25;

/**
 * Utforare - Provider listing for Nacka
 *
 * Uses the national provider index (from hemtjanstguide.se) to display all
 * authorized providers in Nacka kommun with real data including quality metrics.
 *
 * SEO Focus: "hemtjänst anordnare Nacka", "lista hemtjänstföretag Nacka"
 * Features Caire-certified badge system and quality data
 */
const Utforare: React.FC = () => {
  const [searchParams] = useSearchParams();
  const {
    data,
    loading: _loading,
    error: providersError,
  } = useGetProvidersForMunicipalityQuery({
    variables: { municipalitySlug: "nacka" },
  });

  const { data: municipalityData, error: municipalityError } =
    useGetMunicipalityQuery({
      variables: { slug: "nacka" },
    });
  const municipality = municipalityData?.municipality ?? null;

  const providers = useMemo(
    () => data?.providersForMunicipality ?? [],
    [data?.providersForMunicipality],
  );
  const orderedRows = useMemo(
    () =>
      buildOrderedPublicProviderListRows(providers, {
        municipalitySlug: "nacka",
        providerHrefBuilder: buildNackaProviderHref,
        groupHrefBuilder: false,
      }),
    [providers],
  );

  // Calculate stats
  const stats = useMemo(() => summarizePublicProviders(providers), [providers]);

  const maximumPage = Math.max(
    1,
    Math.ceil(orderedRows.length / DEFAULT_PAGE_SIZE),
  );
  const { page, indexable, canonicalUrl } = publicListSeoState(
    searchParams,
    SITE_ORIGIN,
    "/utforare",
    { maxPage: maximumPage },
  );
  const pageRows = orderedRows.slice(
    (page - 1) * DEFAULT_PAGE_SIZE,
    page * DEFAULT_PAGE_SIZE,
  );

  const error = providersError || municipalityError;

  // Convert error to string format for display
  const errorMessage = error
    ? typeof error === "string"
      ? error
      : error?.message || "Okänt fel"
    : null;

  const schemaMarkup = indexable
    ? [
        generateBreadcrumbSchema([
          { name: "Hem", url: "https://nackahemtjanst.se/" },
          { name: "Anordnare", url: CANONICAL_URL },
        ]),
        ...(pageRows.length > 0
          ? [
              // The ItemList has to describe the page being rendered, not
              // always page 1, now that ?page=N is indexable on its own URL.
              generateItemListSchema({
                name: "Hemtjänstutförare i Nacka",
                url: canonicalUrl,
                items: pageRows.map((row, index) => ({
                  name: row.name,
                  url: new URL(
                    row.detailHref,
                    NACKA_PROVIDER_CANONICAL_ORIGIN,
                  ).toString(),
                  position: (page - 1) * DEFAULT_PAGE_SIZE + index + 1,
                })),
              }),
            ]
          : []),
      ]
    : undefined;

  return (
    <>
      <EnhancedSeoHead
        title="Hemtjänst Nacka utförare – jämför anordnare | Nacka Hemtjänst"
        description="Jämför hemtjänstutförare i Nacka kommun: lista över godkända anordnare, kvalitetsdata och moderna system. Välj rätt hemtjänst i Nacka."
        canonicalUrl={canonicalUrl}
        indexable={indexable}
        keywords={[
          "hemtjänst nacka utförare",
          "nacka hemtjänst utförare",
          "hemtjänst anordnare Nacka",
          "lista hemtjänstföretag Nacka",
          "jämför hemtjänst Nacka",
          "nacka hemtjänst recensioner",
        ]}
        contentClassification="Consumer Portal"
        targetAudience="seniors, families"
        coverage="Sweden"
        schemaMarkup={schemaMarkup}
        entities={[{ type: "Place", name: "Nacka" }]}
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-b from-emerald-50 via-white to-white py-16 md:py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-100/20 via-transparent to-blue-100/20" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="bg-white/80 backdrop-blur border border-emerald-100 rounded-2xl p-6 inline-block mb-6 shadow-lg">
            <Users className="w-12 h-12 text-emerald-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-surface-900 tracking-tight mb-6">
            Hemtjänstutförare i Nacka
          </h1>
          <p className="text-xl text-surface-600 max-w-2xl mx-auto leading-relaxed mb-8">
            Nacka kommun har {stats.totalProviders} godkända hemtjänstutförare.
            Jämför kvalitet, kontinuitet och tjänster för att hitta rätt
            anordnare för dig.
          </p>

          {/* Quick Stats */}
          <div className="flex flex-wrap justify-center gap-4">
            <div className="bg-white rounded-xl px-4 py-2 shadow-sm border border-surface-100 flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" />
              <span className="font-semibold text-surface-800">
                {stats.totalProviders} anordnare
              </span>
            </div>
            {stats.avgSatisfaction !== null && (
              <div className="bg-white rounded-xl px-4 py-2 shadow-sm border border-surface-100 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-amber-600" />
                <span className="font-semibold text-surface-800">
                  {stats.avgSatisfaction.toLocaleString("sv-SE", {
                    maximumFractionDigits: 1,
                  })}
                  % snitt nöjdhet
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Provider List */}
      <section className="py-12 px-4 bg-gradient-to-b from-surface-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-surface-900">
              Alla anordnare i Nacka
            </h2>
            {municipality?.providerListUrl && (
              <a
                href={municipality.providerListUrl}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-surface-500 hover:text-emerald-600 flex items-center gap-1"
              >
                Officiell lista
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>

          {error && errorMessage ? (
            <div className="text-center py-12 bg-rose-50 rounded-xl">
              <p className="text-rose-700">
                Kunde inte ladda anordnare: {errorMessage}
              </p>
            </div>
          ) : (
            <ProviderList
              providers={providers}
              stats={stats}
              municipalitySlug="nacka"
              providerHrefBuilder={buildNackaProviderHref}
              groupHrefBuilder={false}
              listSource={municipality?.providerListSource}
              presentation="consumer"
              showCaireIndex
              ranks={{
                municipality: { label: "Nacka" },
                national: { label: "Nationellt" },
              }}
              caireIndexMethodologyHref={CAIRE_INDEX_METHODOLOGY_URL}
            />
          )}
        </div>
      </section>

      {/* Metrics Explanation Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="bg-emerald-100 rounded-2xl p-4 inline-block mb-6">
              <Info className="w-12 h-12 text-emerald-600" />
            </div>
            <h2 className="text-3xl font-bold text-surface-900 mb-4">
              Förstå kvalitetsmätningarna
            </h2>
            <p className="text-lg text-surface-600 max-w-2xl mx-auto">
              Här förklarar vi vad alla siffror och märkningar betyder så att du
              kan göra ett välgrundat val.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* Kontinuitet */}
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <div className="flex items-center gap-3 mb-3">
                <Users className="w-6 h-6 text-blue-600" />
                <h3 className="text-xl font-bold text-surface-900">
                  Kontinuitet
                </h3>
              </div>
              <p className="text-surface-600 mb-4">
                <strong>Vad det betyder:</strong> Antal olika omsorgspersonal
                som en brukare möter under en 14-dagarsperiod. Definition från
                Socialstyrelsens Enhetsundersökningen om äldreomsorg.
              </p>
              <div className="bg-blue-50 rounded-lg p-3 mb-4 border border-blue-200">
                <p className="text-blue-900 text-sm">
                  <strong>Källa:</strong>{" "}
                  <a
                    href="https://www.socialstyrelsen.se/statistik-och-data/oppna-jamforelser/socialtjanst/aldreomsorg/hemtjanst-och-sarskilt-boende/"
                    target="_blank"
                    rel="noreferrer"
                    className="underline hover:text-blue-700"
                  >
                    Socialstyrelsens öppna jämförelser
                  </a>
                </p>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                  <span>
                    <strong>≤12:</strong> Mycket bra kontinuitet (bättre än
                    målet)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span>
                    <strong>13-15:</strong> God kontinuitet (målet enligt
                    Socialstyrelsen)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-rose-500"></div>
                  <span>
                    <strong>16+:</strong> Bör förbättras (nationellt genomsnitt:
                    ~16)
                  </span>
                </div>
              </div>
              <p className="text-surface-600 text-sm mt-4">
                <strong>Varför det är viktigt:</strong> Högre kontinuitet ger
                mer trygghet, bättre relationer och personalen kan snabbare
                upptäcka förändringar i hälsotillståndet. Socialstyrelsen mäter
                detta årligen i sin enhetsundersökning.
              </p>
            </div>

            {/* CaireIndex — same metric as the list (`showCaireIndex`) */}
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <div className="flex items-center gap-3 mb-3">
                <BarChart3 className="w-6 h-6 text-purple-600" />
                <h3 className="text-xl font-bold text-surface-900">
                  CaireIndex
                </h3>
              </div>
              <p className="text-surface-600 mb-4">
                <strong>Vad det betyder:</strong> CAIREs proprietära modellpoäng
                (0–100) från offentlig kvalitets- och verksamhetsdata – inte ett
                myndighetsbetyg. Färgbanden speglar den nuvarande
                datafördelningen.{" "}
                {CAIRE_INDEX_OBSERVED_NATIONAL_CEILING_HINT_SV} Det innebär inte
                att 100 är vanligt.
              </p>
              <ul className="space-y-1 text-sm text-surface-600 mb-4">
                <li>• Brukarnöjdhet</li>
                <li>• Personalkontinuitet</li>
                <li>• Utbildningsnivå</li>
                <li>
                  • Verksamhets- och registersignaler (8 komponenter totalt)
                </li>
              </ul>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                  <span>
                    <strong>≈55+:</strong> Toppskikt i nuvarande datafördelning
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span>
                    <strong>≈45–54:</strong> Stark placering
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span>
                    <strong>≈30–44:</strong> Mellanskikt
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-slate-400"></div>
                  <span>
                    <strong>&lt;30:</strong> Lägre skikt eller tunt underlag
                  </span>
                </div>
              </div>
            </div>

            {/* Nöjdhet */}
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <div className="flex items-center gap-3 mb-3">
                <Award className="w-6 h-6 text-amber-600" />
                <h3 className="text-xl font-bold text-surface-900">
                  Kundnöjdhet
                </h3>
              </div>
              <p className="text-surface-600 mb-4">
                <strong>Vad det betyder:</strong> Andel brukare som är nöjda
                eller mycket nöjda med sin hemtjänst, baserat på årliga
                brukarundersökningar.
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                  <span>
                    <strong>≥85%:</strong> Mycket hög nöjdhet
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span>
                    <strong>75-84%:</strong> God nöjdhet (certifieringskrav)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-rose-500"></div>
                  <span>
                    <strong>&lt;75%:</strong> Bör förbättras
                  </span>
                </div>
              </div>
            </div>

            {/* Antal kunder */}
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <div className="flex items-center gap-3 mb-3">
                <Users className="w-6 h-6 text-indigo-600" />
                <h3 className="text-xl font-bold text-surface-900">
                  Antal kunder
                </h3>
              </div>
              <p className="text-surface-600 mb-4">
                <strong>Vad det betyder:</strong> Ungefärligt antal aktiva
                brukare som utföraren har i Nacka kommun. Visas som intervall
                för att skydda integriteten.
              </p>
              <p className="text-surface-600 text-sm">
                <strong>Varför det är relevant:</strong> Större anordnare kan ha
                mer resurser och erfarenhet, medan mindre kan erbjuda mer
                personlig service. Båda har sina fördelar.
              </p>
            </div>
          </div>

          {/* Extended Info Section */}
          <details className="bg-surface-50 rounded-xl p-6 border border-surface-200">
            <summary className="cursor-pointer flex items-center gap-3 text-lg font-semibold text-surface-900 hover:text-emerald-600 transition-colors">
              <Info className="w-5 h-5" />
              Ytterligare information om anordnare
            </summary>
            <div className="mt-6 space-y-6 pt-6 border-t border-surface-200">
              <div>
                <h4 className="font-semibold text-surface-900 mb-2 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-surface-600" />
                  Verksam sedan
                </h4>
                <p className="text-surface-600 text-sm">
                  Visar vilket år utföraren började verka i Nacka kommun. Längre
                  etablering kan indikera stabilitet och erfarenhet, men nya
                  anordnare kan också erbjuda moderna lösningar.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-surface-900 mb-2 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-surface-600" />
                  System/teknik
                </h4>
                <p className="text-surface-600 text-sm mb-3">
                  Vilket planeringssystem utföraren använder. Olika system har
                  olika funktioner:
                </p>
                <ul className="space-y-1 text-sm text-surface-600 ml-4">
                  <li>
                    •{" "}
                    <strong>
                      <a
                        href="https://carefox.se/"
                        target="_blank"
                        rel="noreferrer"
                        className="text-emerald-600 hover:underline inline-flex items-center gap-1"
                      >
                        Carefox <ExternalLink className="w-3 h-3" />
                      </a>
                      ,{" "}
                      <a
                        href="https://alfaecare.se/alfa-ecare-welfare/"
                        target="_blank"
                        rel="noreferrer"
                        className="text-emerald-600 hover:underline inline-flex items-center gap-1"
                      >
                        eCare <ExternalLink className="w-3 h-3" />
                      </a>
                      ,{" "}
                      <a
                        href="https://www.tieto.com/se/branscher/halsa/lifecare-aldrevard/"
                        target="_blank"
                        rel="noreferrer"
                        className="text-emerald-600 hover:underline inline-flex items-center gap-1"
                      >
                        Lifecare <ExternalLink className="w-3 h-3" />
                      </a>
                      :
                    </strong>{" "}
                    Etablerade system med app-stöd
                  </li>
                  <li>
                    • <strong>Caire:</strong> Modern AI-driven schemaläggning
                  </li>
                  <li>
                    • <strong>Pulsen Combine:</strong> Används ofta av kommunala
                    anordnare
                  </li>
                  <li>
                    • <strong>Manuellt/Excel:</strong> Traditionell planering
                    utan digitala verktyg
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-surface-900 mb-2 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-surface-600" />
                  Ekonomisk information
                </h4>
                <p className="text-surface-600 text-sm">
                  För att skydda integriteten och fokusera på kvalitet visar vi
                  inte omsättning, vinst eller ägarinformation direkt. Dessa
                  uppgifter finns tillgängliga via Allabolag.se om du vill
                  granska utförarens ekonomi. Viktigare är att bedöma kvalitet,
                  kontinuitet och hur väl utföraren passar dina behov.
                </p>
              </div>
            </div>
          </details>
        </div>
      </section>

      {/* Certification Explanations */}
      <section className="py-16 px-4 bg-gradient-to-b from-surface-50 to-white">
        <div className="max-w-5xl mx-auto space-y-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-surface-900 mb-4">
              Förstå märkningarna
            </h2>
            <p className="text-lg text-surface-600">
              Nackahemtjanst.se samarbetar med hemtjänstguide.se för nationell
              kvalitetsgranskning. Vi jämför anordnare på nationell nivå för att
              ge dig bästa möjliga översikten.
            </p>
          </div>

          {/* hemtjänstguide.se Certifierad */}
          <div className="bg-green-50 rounded-2xl p-8 border-2 border-green-300 shadow-lg">
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl p-4 shadow-md">
                <Award className="w-10 h-10 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-green-900 mb-2">
                  hemtjänstguide.se Certifierad
                </h3>
                <p className="text-green-800 font-medium mb-2">
                  Nationell kvalitetsmärkning – helt kostnadsfri
                </p>
                <p className="text-green-700 text-sm mb-4">
                  Nackahemtjanst.se samarbetar med hemtjänstguide.se för att
                  granska och certifiera anordnare baserat på offentliga
                  kvalitetsdata. Vi jämför anordnare på nationell nivå för att
                  säkerställa att du får objektiv information.
                </p>
                <a
                  href="https://hemtjanstguide.se"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-green-700 font-semibold hover:underline"
                >
                  Besök hemtjänstguide.se
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>

            <CertificationExplanation />
          </div>

          {/* Caire.se Certifierad */}
          <div className="bg-emerald-50 rounded-2xl p-8 border-2 border-emerald-300 shadow-lg">
            <div className="flex items-start gap-4 mb-6">
              <CaireBadge size="lg" />
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-emerald-900 mb-2">
                  Caire.se Certifierad
                </h3>
                <p className="text-emerald-800 font-medium mb-2">
                  Premium-märkning för modern AI-teknik och transparens
                </p>
                <p className="text-emerald-700 text-sm mb-4">
                  anordnare med denna märkning använder Caire-plattformen för
                  AI-driven schemaläggning med full transparens och kontroll för
                  dig som brukare. Detta är en teknisk certifiering som
                  verifieras automatiskt via Caire API.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 mb-6">
              <h4 className="font-semibold text-emerald-900 mb-3">
                Vad innebär det för dig?
              </h4>
              <ul className="space-y-2">
                {[
                  "Brukarapp där du och anhöriga kan se schemat i realtid",
                  "Du har kontroll över transparens – se kontinuitet, tidsfönster och uppgifter",
                  "Bättre personalkontinuitet – optimerad schemaläggning ger färre olika personal",
                  "Smartare planering som minskar missade eller försenade besök",
                  "Optimerad schemaläggning ger personal mer tid för dig",
                  "Full transparens på alla kvalitetsmått direkt i appen",
                ].map((benefit, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-emerald-800"
                  >
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-emerald-100 rounded-lg p-4 mb-4">
              <p className="text-emerald-900 text-sm">
                <strong>Skillnad mellan märkningarna:</strong>{" "}
                "hemtjänstguide.se Certifierad" är en gratis kvalitetsmärkning
                baserad på offentliga kvalitetsdata (nöjdhet, kontinuitet,
                index). "Caire.se Certifierad" är en teknisk märkning för
                anordnare som aktivt använder Caire-plattformen med brukarapp,
                AI-driven schemaläggning och full transparens. En anordnare kan
                ha båda märkningarna.
              </p>
            </div>

            <SeoFunnelLink
              href="https://www.caire.se/produkt/funktioner/ai-schemalaggning"
              target="_blank"
              rel="noreferrer"
              tracking={{
                sourceSite: "nackahemtjanst.se",
                sourcePath: "/utforare",
                targetPath: "/produkt/funktioner/ai-schemalaggning",
                anchorCluster: "AI-schemaläggning för hemtjänst",
                ctaType: "editorial",
              }}
              className="inline-flex items-center gap-2 text-emerald-700 font-semibold hover:underline"
            >
              Läs mer om Caire-plattformens AI-schemaläggning
              <ArrowRight className="w-4 h-4" />
            </SeoFunnelLink>
            <div className="mt-4">
              <Link
                to="/utforare/moderna-system"
                className="inline-flex items-center gap-2 text-amber-700 font-semibold hover:underline"
              >
                Läs den lokala guiden om moderna system för utförare i Nacka
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Official Source with Glass Effect */}
      <section className="py-12 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light">
            <p className="text-sm text-surface-600 leading-relaxed mb-4">
              <strong>Viktig information:</strong> Denna webbplats är en
              oberoende guide och är inte en del av Nacka kommun. För den
              officiella listan över alla godkända anordnare, besök{" "}
              <a
                href="https://www.nacka.se/omsorg-stod/senior/hjalp-i-hemmet/hemtjanst/"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-600 font-medium hover:underline"
              >
                Nacka kommuns hemsida
              </a>
              .
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Utforare;
